import { createFileRoute } from "@tanstack/react-router";
import { Section, EyebrowHeading } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { TrustBand } from "@/components/site/TrustBand";
import { HeroVideoLoop } from "@/components/site/wow/HeroVideoLoop";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { Reveal } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { ProcessJourney, type JourneyStep } from "@/components/site/wow/ProcessJourney";
import { WeekInPulse } from "@/components/site/wow/WeekInPulse";
import { CrewRoles } from "@/components/site/wow/CrewRoles";
import { BeforeAfterReveal } from "@/components/site/wow/BeforeAfterReveal";
import { GearStrip } from "@/components/site/wow/GearStrip";
import { LiveCountUp } from "@/components/site/case/LiveCountUp";
import { AnswerFaq } from "@/components/site/aeo/AnswerFaq";
import { toFaqJsonLd } from "@/lib/aeo/answers";
import { aeoGeo, breadcrumbs, howTo } from "@/lib/seo/schema";

const PROCESS_FAQ_IDS = [
  "sv_hur_bestaller_man_film",
  "sv_hur_lang_tid_tar_filmproduktion",
  "sv_hur_snabbt_producerar_fenny",
];

export const Route = createFileRoute("/process")({
  head: () => ({
    meta: [
      { title: "Vår process — Från brief till löpande effekt | Fenny" },
      { name: "description", content: "Fennys process i fem steg: strategi, plan, produktion, leverans, optimering. Så ser resan ut från första mötet till löpande filmflöde — utan timdebitering." },
      { property: "og:title", content: "Vår process — Från brief till löpande effekt | Fenny" },
      { property: "og:description", content: "Fem steg från brief till löpande filmflöde — utan rörig kommunikation eller timdebitering." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/process" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Vår process — Fenny" },
      { name: "twitter:description", content: "Fem steg från brief till löpande filmflöde." },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/process" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/process", name: "Vår process — Från brief till löpande effekt | Fenny", description: "Fennys process i fem steg: strategi, plan, produktion, leverans, optimering. Så ser resan ut från första mötet till löpande filmflöde — utan timdebitering."})) },
      {
        type: "application/ld+json",
        children: JSON.stringify(toFaqJsonLd(PROCESS_FAQ_IDS)),
      },
      { type: "application/ld+json", children: JSON.stringify(breadcrumbs([{ name: "Hem", path: "/" }, { name: "Process", path: "/process" }])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          howTo({
            name: "Så jobbar Fenny — från brief till löpande filmflöde",
            description:
              "Fennys process i fem steg: strategimöte, plan och koncept, produktion, leverans och optimering. Första klippet inom 10–14 dagar.",
            path: "/process",
            steps: journey.map((s) => ({ name: s.title, text: s.body })),
          }),
        ),
      },
    ],
  }),
  component: ProcessPage,
});


const heroImages = [
  "/prime-stills/also-fenny-1.jpg",
  "/prime-stills/pricka-fenny-1.jpg",
  "/prime-stills/wimeck-fenny-1.jpg",
];

const journey: JourneyStep[] = [
  {
    n: "01",
    title: "Strategimöte",
    body: "Ett kostnadsfritt möte där vi förstår era affärsmål, kanaler och målgrupp. Inget säljpitch — vi vill veta vad ni faktiskt vill uppnå.",
    time: "1 möte · ca 45 min",
    image: "/prime-stills/fenny-reklam-film-1.jpg",
  },
  {
    n: "02",
    title: "Plan & koncept",
    body: "Vi sätter en innehållsplan för första kvartalet — format, kanaler, budskap och hooks. Ni ser hela upplägget innan vi rör en kamera.",
    time: "1–2 veckor",
    image: "/prime-stills/raaaabaatar-fenny-1.jpg",
  },
  {
    n: "03",
    title: "Produktion",
    body: "Vi spelar in i block — 1 dag åt gången hos er, på location eller i studio. Effektivt, väl regisserat, med samma team varje gång.",
    time: "1 inspelningsblock / månad",
    image: "/prime-stills/elektronikbranschen-fenny-1.jpg",
  },
  {
    n: "04",
    title: "Leverans",
    body: "Färdiga klipp i alla format ni behöver: 16:9, 9:16, 1:1, 3:4. Levereras i delad mapp, redo att publicera. 2–3 revisionsrundor ingår.",
    time: "Löpande genom månaden",
    image: "/prime-stills/garageportexperten-fenny-3.jpg",
  },
  {
    n: "05",
    title: "Optimering",
    body: "Vi följer upp vad som faktiskt funkar — vilka hooks, format och budskap som drar tummen. Nästa månads plan vässas av insikterna.",
    time: "Månadsavstämning",
    image: "/prime-stills/bolt-fenny-1.jpg",
  },
];

function ProcessPage() {
  return (
    <div>
      {/* Cinematic hero */}
      <section className="relative isolate overflow-hidden">
        <HeroVideoLoop images={heroImages} tone="mint" />

        <div className="mx-auto max-w-6xl px-6 pt-40 pb-24 md:pt-52 md:pb-36 lg:pt-60 lg:pb-44">
          <div className="max-w-3xl">
            <Reveal>
              <LivePulseBadge tone="mint" className="mb-6">
                Vår process
              </LivePulseBadge>
            </Reveal>
            <KineticHeading
              text="Från brief till löpande effekt."
              className="text-5xl font-semibold text-foreground md:text-7xl lg:text-[5.5rem]"
              highlight={{ indices: [3, 4], className: "text-mint" }}
            />
            <Reveal delay={0.4}>
              <p className="mt-8 max-w-2xl text-pretty text-lg text-foreground/85 md:text-xl">
                Fem tydliga steg. Samma team hela vägen. Ingen timdebitering, inga
                överraskningar — bara en jämn produktionsrytm som matchar er affärstakt.
              </p>
            </Reveal>
            <Reveal delay={0.55}>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Magnetic>
                  <Button
                    to="/kontakt"
                    variant="mint"
                    arrow
                    trackId="book_strategy_meeting"
                    trackProps={{ location: "process_hero" }}
                  >
                    Boka strategimöte
                  </Button>
                </Magnetic>
                <Button to="/priser" variant="outline">Se priser</Button>
              </div>
            </Reveal>
            <Reveal delay={0.7}>
              <div className="mt-12 flex items-baseline gap-4">
                <span className="font-serif text-5xl font-semibold text-mint md:text-6xl">
                  <LiveCountUp value={67} />
                </span>
                <span className="text-sm text-muted-foreground">
                  leveranser i år · och vi räknar
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Journey */}
      <Section>
        <div className="mb-16 max-w-2xl">
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Resan</div>
          <h2 className="mt-3 text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
            Fem steg — en <span className="text-mint">produktionspuls</span>.
          </h2>
        </div>
        <ProcessJourney steps={journey} />
      </Section>

      {/* Week in Pulse */}
      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="En typisk vecka"
          title="Avmystifierat: så ser månaden ut."
          lede="Pulse är inte magi — det är en rytm. Här är vad som händer i kalendern, vecka efter vecka."
        />
        <div className="mt-12">
          <WeekInPulse />
        </div>
      </Section>

      {/* Crew */}
      <Section>
        <EyebrowHeading
          eyebrow="Teamet"
          title="Samma ansikten. Varje gång."
          lede="Ni brieffar inte om vem ni är var fjärde månad. Samma fasta team följer er — från första manus till sista grade."
          align="center"
        />
        <div className="mt-12">
          <CrewRoles />
        </div>
      </Section>

      {/* Before / After */}
      <Section className="bg-surface/40">
        <div className="mx-auto max-w-4xl">
          <EyebrowHeading
            eyebrow="Känsla & bildkvalitet"
            title="Skillnaden mellan att spela in — och att leverera."
            lede="Dra slidern. Samma motiv, samma dag. Det är ljussättningen, färgen, kompositionen och ljudet som gör att en bild känns som en produktion — inte en inspelning."
            align="center"
          />

          <div className="mt-10">
            <BeforeAfterReveal
              before={"/prime-stills/raaaabaatar-fenny-1.jpg"}
              after={"/prime-stills/pricka-fenny-1.jpg"}
            />
          </div>
        </div>
      </Section>

      {/* Gear */}
      <Section>
        <EyebrowHeading
          eyebrow="Verktygslådan"
          title="Premium-utrustning. Standard hos oss."
          align="center"
        />
        <div className="mt-10">
          <GearStrip />
        </div>
      </Section>

      {/* Hand-off */}
      <Section className="bg-surface/40">
        <EyebrowHeading
          eyebrow="Hand-off"
          title="Så får ni filerna."
        />
        <div className="mt-12 grid gap-4 md:grid-cols-4">
          {[
            { t: "Delad mapp", b: "Google Drive eller Dropbox — ni får direkt tillgång till alla råfiler och slutleveranser." },
            { t: "Format-matris", b: "Varje klipp levereras i alla beställda format (16:9, 9:16, 3:4, 1:1) — namngivna konsekvent." },
            { t: "Versionering", b: "Tydlig namnkonvention: `klient_kampanj_format_v1`. Inga `final-FINAL-2`-mappar." },
            { t: "Rättigheter", b: "Allt slutmaterial är ert. Vi behåller rätten att visa utvalda klipp i vår portfolio." },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl border border-white/10 bg-background p-6">
              <div className="text-[11px] uppercase tracking-[0.18em] text-mint">{c.t}</div>
              <p className="mt-3 text-sm leading-relaxed text-foreground/85">{c.b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Closing */}
      <Section>
        <div className="overflow-hidden rounded-3xl border border-mint/20 bg-gradient-to-br from-surface via-surface to-mint/[0.06] p-10 md:p-16">
          <EyebrowHeading
            eyebrow="Nästa steg"
            title="Boka mötet — vi pratar process, ni får en plan."
            lede="45 minuter. Inget säljpitch. Ni går därifrån med en konkret skiss på er första månad."
            align="center"
          />
          <div className="mx-auto mt-10 flex max-w-md flex-col gap-3 sm:flex-row sm:justify-center">
            <Magnetic>
              <Button
                to="/kontakt"
                variant="mint"
                arrow
                trackId="book_strategy_meeting"
                trackProps={{ location: "process_cta" }}
              >
                Boka strategimöte
              </Button>
            </Magnetic>
            <Button to="/tjanster" variant="outline">Se alla tjänster</Button>
          </div>
        </div>
      </Section>

      <AnswerFaq
        answerIds={PROCESS_FAQ_IDS}
        eyebrow="Frågor & svar"
        title="Vanliga frågor om processen"
        className="bg-surface/40"
      />

      <TrustBand ctaLocation="process_trust_band" />
    </div>
  );
}
